import { yupResolver } from '@hookform/resolvers/yup';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Refresh from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { SingleDateTimeFormField } from '~components/FormFields/SingleDateTimeFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { SiteSelection } from '~components/Order/SiteSelection/SiteSelection';
import { SystemRoles } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import {
  nameIdNonRequiredSchema,
  nameIdSchemaRequired,
  WaypointSchema,
  waypointSchema,
} from '~constants/regexConst';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useEquipment from '~hooks/useEquipment/useEquipment';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { useOrders } from '~hooks/useOrders/useOrders';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { WayPoint } from '~hooks/useSites/models';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import OrderCustomerInputV2 from '~pages/Sales/Orders/NewOrderForm/OrderCustomerInputV2';
import { createWaypoint } from '~pages/Sales/Orders/NewOrderForm/utils';
import {
  MAX_TRUCK_COUNT,
  NewOrderFormSchemaInterface,
} from '~pages/Sales/Orders/newOrderFormSchema';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { makeOrderTitle } from '~utils/orders';

import { AutocompleteAsyncFormField } from '../FormFields/AutocompleteAsyncFormField';

interface QuickOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const quickOrderIntakeSchema = yup.object().shape({
  user_prompt: yup.string().required(),
});

type QuickOrderIntakeDTO = yup.InferType<typeof quickOrderIntakeSchema>;

const LOAD_AT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const quickOrderFormSchema = yup.object().shape({
  account: nameIdSchemaRequired.required(),
  loadAt: yup.string().required(),
  loadsPerTruck: yup.lazy((val) => {
    return String(val).trim() === ''
      ? yup.string().notRequired()
      : yup
          .number()
          .nullable()
          .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
          .max(25, `${t('form_validation_errors.maximum_is_num', { num: 25 })}`);
  }),
  truckCount: yup
    .number()
    .min(1)
    .max(MAX_TRUCK_COUNT)
    .typeError('Numbers only')
    .required(),
  truckDelay: yup.lazy((val) => {
    return String(val).trim() === ''
      ? yup.string().notRequired()
      : yup.number().notRequired();
  }),
  quantity: yup.number().min(0).required(),
  orderNotes: yup.string().nullable(),
  serviceId: yup.string().required(),
  material: nameIdNonRequiredSchema.notRequired(),
  unitOfMeasure: nameIdNonRequiredSchema.notRequired(),
  requestedEquipmentType: nameIdNonRequiredSchema.notRequired(),
  foremen: yup.array().of(
    yup
      .object()
      .shape({
        id: yup.string().required(),
      })
      .required(),
  ),
  dropOffWayPoint: waypointSchema.notRequired(),
  pickUpWayPoint: waypointSchema.notRequired(),
});

type QuickOrderFormDTO = yup.InferType<typeof quickOrderFormSchema>;

function isAiDraftUnchanged(
  aiDraft: Partial<QuickOrderFormDTO>,
  submitted: QuickOrderFormDTO,
) {
  return Object.entries(aiDraft).every(([field, draftValue]) => {
    if (field === 'loadAt') {
      return dayjs(submitted.loadAt).tz().format(LOAD_AT_FORMAT) === draftValue;
    }

    return isEqual(submitted[field as keyof QuickOrderFormDTO], draftValue);
  });
}

export default function QuickOrderDialog({ isOpen, onClose }: QuickOrderDialogProps) {
  const { intakeOrder } = useOrders();
  const { createOrder } = useOrdersDispatch();
  const { getMaterialsByCompanyIdTypeahead } = useMaterials();
  const { getEquipmentTypesByCompanyIdTypeahead } = useEquipment();
  const getEquipmentTypesByCompanyIdTypeaheadSafe = useCallback(
    async (options: Parameters<typeof getEquipmentTypesByCompanyIdTypeahead>[0]) => {
      try {
        return await getEquipmentTypesByCompanyIdTypeahead(options);
      } catch {
        // Error is already handled in the hook
        return { data: [], pagination: undefined };
      }
    },
    [getEquipmentTypesByCompanyIdTypeahead],
  );
  const { getCompanyUsersTypeahead } = useUsers();
  const { userStore, ordersDispatchStore, toasterStore } = useStores();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const dispatchSmartAutoRefreshFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.dispatchSmartAutoRefresh,
  });
  const shouldAutoOpenCreatedOrder =
    dispatchSmartAutoRefreshFeatureFlag.isReady &&
    dispatchSmartAutoRefreshFeatureFlag.isFeatureFlagEnabled;
  const currentCompany = userStore.currentCompanies[0] || userStore.user.company;
  const [isIntakingOrder, setIsIntakingOrder] = useState(false);
  const [isDraftOrderCreated, setIsDraftOrderCreated] = useState(false);
  const [hints, setHints] = useState<Record<string, string> | null>(null);

  // form for user prompt (intake)
  const intakeFormMethods = useForm<QuickOrderIntakeDTO>({
    resolver: yupResolver(quickOrderIntakeSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      user_prompt: '',
    },
  });

  const {
    control: intakeControl,
    formState: intakeFormState,
    handleSubmit: intakeHandleSubmit,
  } = intakeFormMethods;

  // form for order preview fields
  const orderFormMethods = useForm<QuickOrderFormDTO>({
    resolver: yupResolver(quickOrderFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      account: {
        name: '',
        id: '',
      },
      loadsPerTruck: null,
      truckCount: 1,
      truckDelay: null,
      quantity: 0,
      loadAt: '',
      orderNotes: null,
      serviceId: '',
      material: null,
      requestedEquipmentType: null,
      foremen: [],
      pickUpWayPoint: null,
      dropOffWayPoint: null,
    },
  });

  const {
    control: orderControl,
    formState: orderFormState,
    handleSubmit: orderHandleSubmit,
    reset: resetOrderForm,
    setValue: orderSetValue,
  } = orderFormMethods;

  const aiDraftRef = useRef<Partial<QuickOrderFormDTO> | null>(null);

  const trackAiDraftRejected = useCallback(() => {
    if (aiDraftRef.current) {
      AnalyticsController.track('ai_suggestion_resolved', {
        feature: 'order_intake',
        object_id: null,
        outcome: 'rejected',
      });
      aiDraftRef.current = null;
    }
  }, []);

  const handleClose = () => {
    trackAiDraftRejected();
    orderFormMethods.reset();
    intakeFormMethods.reset();
    setIsDraftOrderCreated(false);
    onClose();
  };

  // Quick Order funnel step 1 of 3: opened → generated → submitted
  useEffect(() => {
    if (isOpen) {
      AnalyticsController.track('quick_order_opened');
    }
  }, [isOpen]);

  const clearOrderForm = useCallback(() => {
    trackAiDraftRejected();
    resetOrderForm();
    setHints(null);
  }, [resetOrderForm, trackAiDraftRejected]);

  const onIntakeSubmit = useCallback(
    async (data: QuickOrderIntakeDTO) => {
      setIsIntakingOrder(true);

      try {
        const intakeResponse = await intakeOrder({
          user_prompt: data.user_prompt,
          openai_model: 'gpt-4o',
        });

        if (!intakeResponse) {
          return;
        }

        const { order, metadata } = intakeResponse;
        clearOrderForm();

        const fieldsToSet = {
          loadsPerTruck: order.loadsPerTruck,
          truckCount: order.truckCount,
          truckDelay: order.truckDelay,
          quantity: order.quantity,
          loadAt: order.loadAt ? order.loadAt.format(LOAD_AT_FORMAT) : undefined,
          orderNotes: order.notes,
          serviceId: order.serviceId,
          unitOfMeasure: order.unitOfMeasure,
        };

        const aiDraft: Partial<QuickOrderFormDTO> = Object.fromEntries(
          Object.entries(fieldsToSet).filter(
            ([, value]) => value !== null && value !== undefined,
          ),
        );

        Object.entries(aiDraft).forEach(([field, value]) => {
          orderSetValue(field as keyof QuickOrderFormDTO, value);
        });
        aiDraftRef.current = aiDraft;

        if (metadata?.hints) {
          setHints(metadata.hints);
        }

        // Funnel step 2: the LLM parsed the prompt into a draft order
        AnalyticsController.track('quick_order_generated', {
          has_hints: Boolean(metadata?.hints),
        });
        setIsDraftOrderCreated(true);
      } finally {
        setIsIntakingOrder(false);
      }
    },
    [clearOrderForm, intakeOrder, orderSetValue],
  );

  const onOrderSubmit = useCallback(
    async (data: QuickOrderFormDTO) => {
      const formData = data as QuickOrderFormDTO;

      const transformedData = {
        account: formData.account,
        loadAt: formData.loadAt,
        service: { id: formData.serviceId },
        truckCount: formData.truckCount,
        truckDelay: formData.truckDelay,
        quantity: formData.quantity,
        loadsPerTruck: formData.loadsPerTruck,
        notes: formData.orderNotes,
        unitOfMeasure: formData.unitOfMeasure,
        material: formData.material,
        requestedEquipmentType: formData.requestedEquipmentType,
        foremen: formData.foremen,
        pickUpWayPoint: formData.pickUpWayPoint,
        dropOffWayPoint: formData.dropOffWayPoint,
      } as unknown as NewOrderFormSchemaInterface;

      try {
        const createdOrder = await createOrder(transformedData);

        if (createdOrder?.id) {
          // Funnel step 3: an order actually came out of the AI flow. The
          // backend's order_created can't see the source UI — this can.
          // order_id is the UUID so it joins to the BE's order_created events.
          AnalyticsController.track('quick_order_submitted', {
            order_id: createdOrder.id,
            order_friendly_id: createdOrder.orderId,
          });

          if (aiDraftRef.current) {
            AnalyticsController.track('ai_suggestion_resolved', {
              feature: 'order_intake',
              object_id: createdOrder.id,
              outcome: isAiDraftUnchanged(aiDraftRef.current, formData)
                ? 'accepted'
                : 'edited',
            });
            aiDraftRef.current = null;
          }
          if (shouldAutoOpenCreatedOrder) {
            ordersDispatchStore.setDeepLinkedOrder(createdOrder, { source: 'created' });
            toasterStore.push(
              alert(
                t('dispatch.order.created', { name: createdOrder.orderId }),
                AlertTypes.success,
              ),
            );
          } else {
            toasterStore.push(
              alert(
                <Box
                  sx={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '290px',
                  }}
                >
                  <Typography sx={{ fontSize: '12px' }}>
                    {t('dispatch.dispatch_v2.order_toast.order_created')}
                  </Typography>

                  <Typography>{makeOrderTitle(createdOrder, true)}</Typography>

                  <SmallButton color="brandV2Green" size="small" variant="contained">
                    {t('dispatch.dispatch_v2.order_toast.view_order')}
                  </SmallButton>
                </Box>,
                AlertTypes.success,
                null,
                () =>
                  ordersDispatchStore.setDeepLinkedOrder(createdOrder, {
                    source: 'created',
                  }),
              ),
              true,
            );
          }

          handleClose();
        }
      } catch {
        // Error is handled by the hook
      }
    },
    [
      handleClose,
      createOrder,
      ordersDispatchStore,
      orderFormState,
      shouldAutoOpenCreatedOrder,
      toasterStore,
    ],
  );

  return (
    <FormProvider {...orderFormMethods}>
      <Dialog
        isOpen={isOpen}
        maxWidth="lg"
        onClose={handleClose}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleClose}>
          {t('dispatch.dispatch_v2.quick_order')}
        </DialogHeader>

        <DialogContent
          sx={(theme) => ({
            bgcolor: theme.semantic.surfaceSunken,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          })}
        >
          <Grid container spacing={2}>
            <FormProvider {...intakeFormMethods}>
              <Grid size={{ xs: 6 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="h6">
                        {t('order.quick_order.describe_order')}
                      </Typography>
                      <Typography variant="body2">
                        {t('order.quick_order.prompt_helper_text')}
                      </Typography>
                      <TextFormField
                        placeholder={`${t('order.quick_order.prompt_placeholder')}`}
                        control={intakeControl}
                        errors={intakeFormState.errors}
                        name={'user_prompt'}
                        multiline
                        rows={8}
                        isRequired={true}
                      />
                      <SmallLoadingButton
                        disabled={isIntakingOrder}
                        loading={isIntakingOrder}
                        loadingPosition="start"
                        onClick={intakeHandleSubmit(onIntakeSubmit)}
                        startIcon={<AutoAwesomeIcon />}
                        sx={{ width: 'fit-content' }}
                      >
                        {t('order.quick_order.generate')}
                      </SmallLoadingButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </FormProvider>

            <Grid size={{ xs: 6 }}>
              {isDraftOrderCreated && (
                <Card>
                  <CardContent>
                    <Grid
                      container
                      columnSpacing={2}
                      rowSpacing={1}
                      sx={{
                        opacity: isIntakingOrder ? '0.4' : '1',
                        pointerEvents: isIntakingOrder ? 'none' : 'auto',
                      }}
                    >
                      <Grid
                        sx={{
                          alignItems: 'flex-start',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        size={{ xs: 12 }}
                      >
                        <Typography variant="h6">
                          {t('order.quick_order.order_preview')}
                        </Typography>
                        <SmallButton
                          onClick={clearOrderForm}
                          size="small"
                          startIcon={<Refresh />}
                          variant="text"
                          color="brandV2TreadGray3"
                        >
                          {t('common.clear')}
                        </SmallButton>
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        <OrderCustomerInputV2
                          companyId={currentCompany?.id}
                          prefilledValue={hints?.account ?? undefined}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <SingleDateTimeFormField
                          dateLabel={`${t('order.form.start_date')}`}
                          timeLabel={`${t('order.form.start_time')}`}
                          control={orderControl}
                          errors={orderFormState.errors}
                          name={'loadAt'}
                          isRequired={true}
                        />
                      </Grid>
                      {(hints?.pickup_location || hints?.dropoff_location) && (
                        <Grid size={{ xs: 12 }}>
                          <Divider sx={{ my: 2 }} />
                          <SiteSelection
                            showMap={false}
                            allowSuggestions={false}
                            companyId={currentCompany?.id}
                            hideAddDefaultSiteAction={true}
                            prefilledPickUpValue={hints?.pickup_location}
                            prefilledDropOffValue={hints?.dropoff_location}
                            onPickUpSiteChange={(site) => {
                              let newPickUpWaypoint: WayPoint | null = null;
                              if (site) {
                                newPickUpWaypoint = createWaypoint(
                                  site,
                                  WaypointType.PICKUP,
                                  10,
                                  undefined,
                                );
                              }

                              orderSetValue(
                                'pickUpWayPoint',
                                newPickUpWaypoint as WaypointSchema,
                                {
                                  shouldDirty: true,
                                },
                              );
                            }}
                            onDropOffSiteChange={(site) => {
                              let newDropOffWaypoint = null;
                              if (site) {
                                newDropOffWaypoint = createWaypoint(
                                  site,
                                  WaypointType.DROP_OFF,
                                  20,
                                  undefined,
                                );
                              }

                              orderSetValue(
                                'dropOffWayPoint',
                                newDropOffWaypoint as WaypointSchema,
                                {
                                  shouldDirty: true,
                                },
                              );
                            }}
                          />
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                      )}
                      <Grid size={{ xs: 6 }}>
                        <TextFormField
                          control={orderControl}
                          errors={orderFormState.errors}
                          name="truckCount"
                          label={`${t('form_fields.trucks')}`}
                          type="number"
                          isRequired={true}
                        />
                      </Grid>
                      {hints?.equipment_type !== null &&
                        hints?.equipment_type !== undefined && (
                          <Grid size={{ xs: 6 }}>
                            <AutocompleteAsyncFormField
                              control={orderControl}
                              errors={orderFormState.errors}
                              name="requestedEquipmentType"
                              asyncCallback={getEquipmentTypesByCompanyIdTypeaheadSafe}
                              extraRequestOptions={{
                                companyId: currentCompany?.id,
                              }}
                              getValue={(item) => item.id}
                              getLabel={(item) => item.name || ''}
                              label={`${t('form_fields.truck_type')}`}
                              clearable={false}
                              prefilledValue={hints?.equipment_type}
                            />
                          </Grid>
                        )}

                      {orderFormMethods.getValues('quantity') !== null &&
                        orderFormMethods.getValues('quantity') !== undefined && (
                          <>
                            <Grid size={{ xs: 6 }}>
                              <TextFormField
                                control={orderControl}
                                errors={orderFormState.errors}
                                name="loadsPerTruck"
                                label={`${t('form_fields.loads_per_truck')}`}
                                isRequired={false}
                              />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <TextFormField
                                control={orderControl}
                                errors={orderFormState.errors}
                                label={`${t('form_fields.ordered_quantity')}`}
                                name="quantity"
                                type="number"
                              />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <AutocompleteFormField
                                control={orderControl}
                                errors={orderFormState.errors}
                                name="unitOfMeasure"
                                list={unitOfMeasureOptions}
                                label={`${t('form_fields.unit')}`}
                                getValue={(item) => item.id}
                                getLabel={(item) => item.name || ''}
                              />
                            </Grid>
                          </>
                        )}

                      {orderFormMethods.getValues('truckDelay') !== null &&
                        orderFormMethods.getValues('truckDelay') !== undefined && (
                          <Grid size={{ xs: 12 }}>
                            <TextFormField
                              control={orderControl}
                              errors={orderFormState.errors}
                              name="truckDelay"
                              label={`${t('form_fields.truck_stagger')}`}
                              type="number"
                            />
                          </Grid>
                        )}

                      {hints?.material !== null && hints?.material !== undefined && (
                        <Grid size={{ xs: 12 }}>
                          <AutocompleteAsyncFormField
                            prefilledValue={hints?.material}
                            control={orderControl}
                            errors={orderFormState.errors}
                            name="material"
                            asyncCallback={getMaterialsByCompanyIdTypeahead}
                            extraRequestOptions={{ companyId: currentCompany?.id }}
                            getValue={(item) => item.id}
                            getLabel={(item) => item.name || ''}
                            label={`${t('form_fields.material')}`}
                            clearable={true}
                          />
                        </Grid>
                      )}

                      {hints?.contact !== null && hints?.contact !== undefined && (
                        <Grid size={{ xs: 12 }}>
                          <AutocompleteAsyncFormField
                            control={orderControl}
                            errors={orderFormState.errors}
                            multiple
                            filterSelectedOptions={false}
                            name="foremen"
                            getValue={(user) => user.id}
                            getLabel={(user: User) =>
                              `${user.firstName} ${user.lastName}`
                            }
                            label={`${t('form_fields.assigned_foreman')}`}
                            asyncCallback={getCompanyUsersTypeahead}
                            extraRequestOptions={{
                              companyId: currentCompany?.id,
                              roleName: SystemRoles.FOREMAN,
                            }}
                            prefilledValue={hints?.contact}
                          />
                        </Grid>
                      )}

                      {orderFormMethods.getValues('orderNotes') && (
                        <Grid size={{ xs: 12 }}>
                          <TextFormField
                            placeholder={`${t('dispatch.order.add_note')}`}
                            control={orderControl}
                            errors={orderFormState.errors}
                            label={`${t('dispatch.order.order_notes')}`}
                            name={'orderNotes'}
                            multiline
                            rows={5}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isIntakingOrder}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            disabled={isIntakingOrder || ordersDispatchStore.isSavingOrder}
            loading={isIntakingOrder || ordersDispatchStore.isSavingOrder}
            onClick={orderHandleSubmit(onOrderSubmit)}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
